import { useEffect } from 'react';

/**
 * Anima los elementos `.reveal`, `.reveal-left` y `.reveal-right` cuando entran
 * al viewport, agregando la clase `.visible`. Se observa cada elemento una sola
 * vez. Re-corre cuando cambia `dep` (útil cuando llegan datos async y nuevos
 * elementos `.reveal*` entran al DOM).
 */
export function useReveal(dep: unknown = null) {
  useEffect(() => {
    const elements = document.querySelectorAll<HTMLElement>(
      '.reveal:not(.visible), .reveal-left:not(.visible), .reveal-right:not(.visible)',
    );
    if (elements.length === 0) return;

    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            obs.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -50px 0px' },
    );

    elements.forEach((el) => obs.observe(el));

    // Safety net: si algún elemento sigue sin .visible tras 800ms
    // (p. ej. está fuera de viewport en mobile, o el IO no disparó), forzar visibilidad.
    // Evita el caso en que un cambio de filtro deja productos en blanco.
    const safety = window.setTimeout(() => {
      document
        .querySelectorAll<HTMLElement>(
          '.reveal:not(.visible), .reveal-left:not(.visible), .reveal-right:not(.visible)',
        )
        .forEach((el) => el.classList.add('visible'));
    }, 800);

    return () => {
      window.clearTimeout(safety);
      obs.disconnect();
    };
  }, [dep]);
}
